import { SupplierThread, ThreadMessage } from '../../components/forms/outreach/types';
import { hoursAgo } from './outreachFixtures';

/**
 * A conversation long enough to prove two things at once in the gallery: the
 * change ribbon scrolls WITH the reply that caused it (it is anchored mid-list,
 * so it leaves the viewport as the buyer scrolls) and the messages own the
 * pane's height rather than sharing it with a stack of cards.
 *
 * Same supplier and component as the main Kaisen fixture, so the four analyst
 * actions match it on the component + supplier pair. Fixtures only.
 */
const say = (id: string, sender: 'buyer' | 'supplier', timestamp: string, text: string): ThreadMessage => ({
  id,
  sender,
  senderName: sender === 'buyer' ? 'You' : 'Kaisen Motion Systems',
  timestamp,
  text,
});

const MESSAGES: ThreadMessage[] = [
  say('lm1', 'buyer', 'Mon 09:04', 'Subject: Brushless DC motor — 240 units\n\nPlease quote unit price at 100 and 240 units, with lead time at each.'),
  say('lm2', 'supplier', 'Mon 14:20', 'Thank you for the enquiry. Our engineering team is reviewing the specification and will revert within two working days.'),
  say('lm3', 'supplier', 'Tue 11:35', 'Initial pricing: ₹1,380/unit at 100 pcs, ₹1,310/unit at 240 pcs. Lead time 16 weeks ex-works.'),
  say('lm4', 'buyer', 'Tue 12:02', 'Sixteen weeks lands after our build slot closes. Can you look again at lead time at 240 units?'),
  say('lm5', 'supplier', 'Wed 07:15', 'We are checking capacity with our winding line for an earlier slot.'),
  say('lm6', 'buyer', 'Wed 11:02', 'Understood. Price at 240 units is also 19% over our target — both need another look.'),
  say('lm7', 'supplier', 'Thu 06:48', 'Revised offer: ₹1,240/unit at 240 pcs, 14 weeks. Price valid until 29 August. We can release 120 pcs at week 10 if the schedule is tight.'),
  say('lm8', 'buyer', 'Thu 09:30', 'Noted, thank you. Confirming the balance of the order still ships complete at week 14?'),
  say('lm9', 'supplier', 'Thu 15:12', 'Correct — 120 pcs at week 10 and the remaining 120 pcs at week 14, single despatch each.'),
  say('lm10', 'buyer', 'Fri 08:44', 'And the 14-week figure assumes the 240-unit price, not the 100-unit one?'),
  say('lm11', 'supplier', 'Fri 10:05', 'Yes. The 14-week slot is reserved against the 240-unit order only.'),
  say('lm12', 'buyer', 'Fri 10:40', 'Understood. Is the quotation you sent the signed version?'),
  say('lm13', 'supplier', 'Fri 16:22', 'The attached PDF is our standard proforma. A signed quotation can follow once quantities are frozen.'),
  say('lm14', 'supplier', 'today 06:10', 'Following up — the week 10 slot is held for us until Friday, after which it releases to the queue.'),
];

export const LONG_THREAD: SupplierThread = {
  id: 't-kaisen-long',
  componentId: 'c-motor',
  supplierName: 'Kaisen Motion Systems',
  hasContactEmail: true,
  componentName: 'Brushless DC motor',
  quantity: 240,
  targetPrice: 1100,
  targetPriceLabel: '₹1,100 / unit',
  status: 'replied',
  summary: 'Revised offer: ₹1,240/unit at 240 pcs, 14 weeks.',
  lastActivityIso: hoursAgo(2),
  quote: { unitPrice: 1240, currency: 'INR', leadTimeWeeks: 14, moq: 100, quotedAtIso: hoursAgo(30), rounds: 2 },
  // Anchored to the seventh message, not the last — so the ribbon sits in the
  // middle of the scroll and moves with it.
  // Deliberately long values and statements: everything that clips on this
  // ribbon has to say so on hover, and short entries have to stay quiet.
  changeHistory: [
    { field: 'QUOTED_PRICE', previousValue: '1310', newValue: '1240', sourceMessageId: 'lm7', condition: null },
    {
      field: 'PAYMENT_TERMS',
      previousValue: '100% against proforma before despatch',
      newValue: '30% advance, 70% net 30 days from despatch date',
      sourceMessageId: 'lm7',
      condition: 'if the order is confirmed by Friday and the week 10 slot is taken as offered',
    },
    { field: 'LEAD_TIME', previousValue: '16', newValue: '14', sourceMessageId: 'lm7', condition: null },
  ],
  commitments: [
    {
      type: 'PARTIAL_SHIPMENT',
      statement: 'If the schedule is critical, we can ship 120 units in 10 weeks from our winding line and the balance at week 14',
      sourceMessageId: 'lm7',
    },
    { type: 'CAPACITY', statement: 'All units are hub-balanced to under 0.15 grams', sourceMessageId: 'lm7' },
    { type: 'VALIDITY', statement: 'Price valid until 29 August', sourceMessageId: 'lm7' },
  ],
  extractionStatus: 'OK',
  messages: MESSAGES,
};

export default LONG_THREAD;
